import { useEffect, useState } from 'react';
import { getCourse } from '../api/courses';

// One request per course while it is in flight: the top-bar breadcrumb and a course sub-page ask
// for the same title at the same moment. Settled requests are dropped, so a renamed course shows
// its new title on the next visit.
const inFlight = new Map();

function fetchTitle(courseId) {
  if (!inFlight.has(courseId)) {
    const request = Promise.resolve()
      .then(() => getCourse(courseId))
      .then((course) => (typeof course?.title === 'string' ? course.title.trim() : ''))
      .finally(() => inFlight.delete(courseId));
    inFlight.set(courseId, request);
  }
  return inFlight.get(courseId);
}

/**
 * The title of a course, for the eyebrow of a standalone sub-page header (student progress,
 * drop-off, ...) and the top-bar breadcrumb: "Mathematics Basics" above "O'quvchi progressi" tells
 * staff which course they are looking at. Cosmetic only — returns '' while loading and when the
 * course cannot be read (a mentor who does not own it gets a 403), so the page keeps its generic
 * parent label.
 *
 *   const courseTitle = useCourseTitle(courseId);
 *   <p className="page-eyebrow">{courseTitle || t('...parent')}</p>
 */
export function useCourseTitle(courseId) {
  const [title, setTitle] = useState('');

  useEffect(() => {
    let cancelled = false;
    setTitle('');
    if (!courseId) return undefined;
    fetchTitle(courseId)
      .then((next) => {
        if (!cancelled) setTitle(next);
      })
      .catch(() => {
        // The generic parent label stays.
      });
    return () => { cancelled = true; };
  }, [courseId]);

  return title;
}

export default useCourseTitle;
